"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Pencil, Save, Star, StarOff, Mail, Phone, Globe, DollarSign, HeadphonesIcon, Clock, BadgeCheck, Loader2, XCircle, Trash, Maximize2, Minimize2, Info, UserRound, CreditCard } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from "@mantis-sas/ui";
import {
  createSupplier,
  getSupplierById,
  updateSupplier,
  deleteSupplier
} from "../../../../ApiCalls/mantisFlowsSuppliers";
import Select from "react-select";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";

// Custom styles for PhoneInput to ensure borders are visible

export default function SupplierModal({
  supplier,
  mode,
  onClose,
  onEditMode,
  onSupplierCreated,
  onSupplierDeleted,
  clientId
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    country: "",
    contact_email: "",
    phone: "",
    currency: "COP",
    language: "",
    description: "",
    capabilities: "",
    typicalleadtime: "",
    nit: "",
    is_favorite: false,
    rating_service: 0,
    rating_punctuality: 0,
    rating_quality: 0,
    rating_overall: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const {toast} = useToast();

  // Helper function to safely format rating values
  const formatRating = (rating) => {
    const numRating = Number(rating) || 0;
    return numRating.toFixed(1);
  };

  // Lista de países (normalmente vendría de una API)
  const countries = [
    "Colombia",
    "México",
    "Estados Unidos",
    "España",
    "Brasil",
    "Argentina",
    "Chile",
    "Perú",
    "Canadá"
  ];
  const currencies = [
    "COP",
    "USD",
    "EUR",
    "MXN",
    "BRL",
    "ARS",
    "CLP",
    "PEN",
    "CAD"
  ];
  const lenguages = [
    "EN",
    "ES",
    "FR",
    "DE",
    "IT",
    "PT",
    "AR",
    "CH",
    "PE",
    "CA"
  ];
  // Preparar opciones para react-select
  const countryOptions = countries.map((country) => ({
    value: country,
    label: country
  }));
  const currencyOptions = currencies.map((currency) => ({
    value: currency,
    label: currency
  }));
  const languageOptions = lenguages.map((language) => ({
    value: language,
    label: language
  }));
  // Función para formatear capabilities al cargar datos
  const formatCapabilitiesForDisplay = (capabilities) => {
    if (!capabilities) return [];

    try {
      // Si es un string JSON, lo parseamos
      if (typeof capabilities === "string") {
        const parsed = JSON.parse(capabilities);
        return Array.isArray(parsed) ? parsed : [capabilities];
      }

      // Si ya es un array, lo devolvemos
      if (Array.isArray(capabilities)) {
        return capabilities;
      }

      // En caso contrario, lo convertimos a string y lo devolvemos como un array de un elemento
      return [String(capabilities)];
    } catch (e) {
      // Si hay error al parsear, intentamos dividir por comas
      return typeof capabilities === "string"
        ? capabilities
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : [];
    }
  };

  // Cargar datos del proveedor
  useEffect(() => {
    if (mode === "view" || mode === "edit") {
      if (supplier) {
        if (mode === "view") {
          setFormData({
            name: supplier.name || "",
            country: supplier.country || "",
            contact_email: supplier.contact_email || "",
            phone: supplier.phone || "",
            currency: supplier.currency || "COP",
            description: supplier.description || "",
            capabilities:
              formatCapabilitiesForDisplay(supplier.capabilities) || "",
            typicalleadtime: supplier.typicalleadtime || "",
            nit: supplier.nit || "",
            is_favorite: supplier.is_favorite || false,
            rating_service: supplier.rating_service || 0,
            rating_punctuality: supplier.rating_punctuality || 0,
            rating_quality: supplier.rating_quality || 0,
            rating_overall: supplier.rating_overall || 0,
            language: supplier.language || ""
          });
        } else {
          // Para edición, obtener datos más recientes
          loadSupplierDetails(supplier.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier, mode]);

  // Cargar detalles del proveedor desde la API
  const loadSupplierDetails = async (supplierId) => {
    try {
      setLoading(true);
      const response = await getSupplierById(clientId, supplierId);

      if (response.success && response.data) {
        setFormData({
          name: response.data.name || "",
          country: response.data.country || "",
          contact_email: response.data.contact_email || "",
          phone: response.data.phone || "",
          currency: response.data.currency || "COP",
          description: response.data.description || "",
          capabilities:
            formatCapabilitiesForDisplay(response.data.capabilities) || "",
          typicalleadtime: response.data.typicalleadtime || "",
          nit: response.data.nit || "",
          is_favorite: response.data.is_favorite || false,
          rating_service: response.data.rating_service || 0,
          rating_punctuality: response.data.rating_punctuality || 0,
          rating_quality: response.data.rating_quality || 0,
          rating_overall: response.data.rating_overall || 0,
          language: response.data.language || ""
        });
      } else {
        toast({
          title: "Error",
          description: "Error al cargar detalles del proveedor",
          variant: "destructive",
          className:
            "bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800"
        });
      }
    } catch (error) {
      console.error("Error al cargar detalles del proveedor:", error);
      toast({
        title: "Error",
        description: "Error al cargar detalles del proveedor",
        variant: "destructive",
        className:
          "bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800"
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const {name, value} = e.target;

    setFormData({
      ...formData,
      [name]: value
    });

    // Limpiar error de validación al editar
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  // Manejar cambios en el teléfono
  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone: value
    });
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "El nombre es requerido";
    }

    if (!formData.contact_email.trim()) {
      errors.contact_email = "El email es requerido";
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      errors.contact_email = "El email no es válido";
    }

    if (!formData.country) {
      errors.country = "El país es requerido";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manejar guardado de proveedor
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Calcular el rating general como el promedio de los tres ratings específicos
      const calculatedOverall =
        ((parseFloat(formData.rating_service) || 0) +
          (parseFloat(formData.rating_punctuality) || 0) +
          (parseFloat(formData.rating_quality) || 0)) /
        3;

      // Crear una copia del formData con el rating_overall actualizado
      const updatedFormData = {
        ...formData,
        rating_overall: calculatedOverall
      };

      // Crear o actualizar proveedor
      if (mode === "create") {
        const response = await createSupplier(clientId, updatedFormData);

        if (response && response.success) {
          toast({
            title: "Proveedor creado",
            description: "El proveedor ha sido creado exitosamente",
            variant: "default",
            className:
              "bg-white border-gray-200 dark:bg-slate-800 dark:border-slate-700"
          });

          // Pass the newly created supplier data to the callback
          onSupplierCreated?.(response.data);

          // Close modal after a brief delay to ensure refresh completes
          setTimeout(() => {
            onClose();
          }, 100);
        } else {
          throw new Error(response?.message || "Error al crear el proveedor");
        }
      } else {
        const response = await updateSupplier(
          clientId,
          supplier.id,
          updatedFormData
        );

        if (response && response.success) {
          toast({
            title: "Proveedor actualizado",
            description: "El proveedor ha sido actualizado exitosamente",
            variant: "default",
            className:
              "bg-white border-gray-200 dark:bg-slate-800 dark:border-slate-700"
          });
          onClose();
        } else {
          throw new Error(
            response?.message || "Error al actualizar el proveedor"
          );
        }
      }
    } catch (error) {
      console.error("Error al guardar proveedor:", error);
      toast({
        title: "Error",
        description: error.message || "Error al guardar proveedor",
        variant: "destructive",
        className:
          "bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar eliminación de proveedor
  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      const response = await deleteSupplier(clientId, supplier.id);

      if (response && response.success) {
        toast({
          title: "Proveedor eliminado",
          description: "El proveedor ha sido eliminado exitosamente",
          variant: "default",
          className:
            "bg-white border-gray-200 dark:bg-slate-800 dark:border-slate-700"
        });

        setShowDeleteDialog(false);
        onClose();

        // Call the callback to refresh the suppliers list
        if (onSupplierDeleted) {
          onSupplierDeleted(supplier.id);
        }
      } else {
        throw new Error(response?.message || "Error al eliminar el proveedor");
      }
    } catch (error) {
      console.error("Error al eliminar proveedor:", error);
      toast({
        title: "Error",
        description: error.message || "Error al eliminar proveedor",
        variant: "destructive",
        className:
          "bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar favorito
  const handleToggleFavorite = () => {
    setFormData({
      ...formData,
      is_favorite: !formData.is_favorite
    });
  };

  const isReadOnly = mode === "view";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 rounded">
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm rounded"
          onClick={onClose}
        />

        <motion.div
          initial={{opacity: 0, scale: 0.95}}
          animate={{opacity: 1, scale: 1}}
          exit={{opacity: 0, scale: 0.95}}
          transition={{duration: 0.2}}
          className={`bg-white dark:bg-slate-900 relative rounded shadow-lg overflow-hidden w-full max-w-4xl z-50 max-h-[90vh] flex flex-col ${
            isFullscreen
              ? "fixed inset-0 rounded-none max-w-none max-h-none"
              : ""
          }`}
        >
          {/* Cabecera */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {mode === "create" ? "Nuevo Proveedor" : formData.name}
            </h2>
            <div className="flex items-center gap-2">
              {mode === "view" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEditMode}
                  className="text-slate-700 dark:text-slate-300"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar
                </Button>
              )}

              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="text-slate-700 dark:text-slate-300"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={onClose}
                className="text-slate-700 dark:text-slate-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Indicador de carga */}
          {loading && (
            <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 flex items-center justify-center z-10">
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-2" />
                <span className="text-slate-800 dark:text-slate-200">
                  Cargando detalles...
                </span>
              </div>
            </div>
          )}

          {/* Contenido principal */}
          <div className="flex-1 overflow-y-auto p-6">
            <Tabs
              defaultValue="general"
              value={activeTab}
              onValueChange={setActiveTab}
            >
              <TabsList className="mb-6">
                <TabsTrigger
                  value="general"
                  className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded"
                >
                  <Info className="h-4 w-4 mr-2" />
                  General
                </TabsTrigger>
                <TabsTrigger
                  value="ratings"
                  className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded"
                >
                  <Star className="h-4 w-4 mr-2" />
                  Calificaciones
                </TabsTrigger>
              </TabsList>

              {/* Contenido de la pestaña General */}
              <TabsContent value="general" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Columna izquierda */}
                  <div className="space-y-4">
                    {/* Nombre */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="flex items-center text-slate-700 dark:text-slate-300 rounded"
                      >
                        <UserRound className="h-4 w-4 mr-2 text-slate-400" />
                        Nombre del Proveedor
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`bg-white dark:bg-slate-800 ${
                            validationErrors.name
                              ? "border-red-500 focus:ring-red-500"
                              : ""
                          }`}
                          placeholder="Ingresa el nombre del proveedor"
                          readOnly={isReadOnly}
                        />
                        {validationErrors.name && (
                          <p className="mt-1 text-xs text-red-500">
                            {validationErrors.name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="contact_email"
                        className="flex items-center text-slate-700 dark:text-slate-300 rounded"
                      >
                        <Mail className="h-4 w-4 mr-2 text-slate-400" />
                        Email de Contacto
                      </Label>
                      <div className="relative">
                        <Input
                          id="contact_email"
                          name="contact_email"
                          type="email"
                          value={formData.contact_email}
                          onChange={handleInputChange}
                          className={`pl-10 bg-white dark:bg-slate-800 ${
                            validationErrors.contact_email
                              ? "border-red-500 focus:ring-red-500"
                              : ""
                          }`}
                          placeholder="example@email.com"
                          readOnly={isReadOnly}
                        />
                        {validationErrors.contact_email && (
                          <p className="mt-1 text-xs text-red-500">
                            {validationErrors.contact_email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Teléfono */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="phone"
                        className="flex items-center text-slate-700 dark:text-slate-300 rounded "
                      >
                        <Phone className="h-4 w-4 mr-2 text-slate-400" />
                        Teléfono
                      </Label>
                      <div
                        className={`w-full border rounded-lg transition-all duration-200 ${
                          isPhoneFocused
                            ? "border-orange-500 ring-2 ring-orange-500/20"
                            : "border-slate-300 dark:border-slate-600"
                        }`}
                      >
                        <PhoneInput
                          international
                          defaultCountry="CO"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          onFocus={() => setIsPhoneFocused(true)}
                          onBlur={() => setIsPhoneFocused(false)}
                          className="!border-none !shadow-none"
                          inputProps={{
                            placeholder: "Número de teléfono",
                            readOnly: isReadOnly,
                            className:
                              "!border-none !outline-none !shadow-none w-full px-4 py-3 bg-transparent text-slate-900 dark:text-slate-100"
                          }}
                        />
                      </div>
                    </div>

                    {/* NIT */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="nit"
                        className="flex items-center text-slate-700 dark:text-slate-300 rounded"
                      >
                        <CreditCard className="h-4 w-4 mr-2 text-slate-400" />
                        NIT / ID Fiscal
                      </Label>
                      <Input
                        id="nit"
                        name="nit"
                        value={formData.nit}
                        onChange={handleInputChange}
                        className="bg-white dark:bg-slate-800"
                        placeholder="ID Fiscal"
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>

                  {/* Columna derecha */}
                  <div className="space-y-4">
                    {/* País con React-Select */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="country"
                        className="flex items-center text-slate-700 dark:text-slate-300 rounded"
                      >
                        <Globe className="h-4 w-4 mr-2 text-slate-400" />
                        País
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      {isReadOnly ? (
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-2 text-slate-400" />
                          <span className="text-slate-700 dark:text-slate-300">
                            {formData.country || "No especificado"}
                          </span>
                        </div>
                      ) : (
                        <div className="relative">
                          <Select
                            options={countryOptions}
                            value={
                              countryOptions.find(
                                (option) => option.value === formData.country
                              ) || null
                            }
                            onChange={(selectedOption) => {
                              setFormData({
                                ...formData,
                                country: selectedOption.value
                              });
                              if (validationErrors.country) {
                                setValidationErrors({
                                  ...validationErrors,
                                  country: null
                                });
                              }
                            }}
                            placeholder="Selecciona un país"
                            className={
                              validationErrors.country ? "border-red-500" : ""
                            }
                            styles={{
                              control: (base) => ({
                                ...base,
                                backgroundColor: "white",
                                borderColor: validationErrors.country
                                  ? "#ef4444"
                                  : "#e2e8f0"
                              }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "white"
                              })
                            }}
                          />
                          {validationErrors.country && (
                            <p className="mt-1 text-xs text-red-500">
                              {validationErrors.country}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Moneda con React-Select */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="currency"
                        className="text-slate-700 dark:text-slate-300 rounded"
                      >
                        Moneda Preferida
                      </Label>
                      {isReadOnly ? (
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-2 text-slate-400" />
                          <span className="text-slate-700 dark:text-slate-300">
                            {formData.currency || "COP"}
                          </span>
                        </div>
                      ) : (
                        <Select
                          options={currencyOptions}
                          value={
                            currencyOptions.find(
                              (option) => option.value === formData.currency
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            setFormData({
                              ...formData,
                              currency: selectedOption.value
                            });
                          }}
                          placeholder="Selecciona una moneda"
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: "white"
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: "white"
                            })
                          }}
                        />
                      )}
                    </div>

                    {/* Language with React-Select */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="language"
                        className="flex items-center text-slate-700 dark:text-slate-300 rounded"
                      >
                        <Globe className="h-4 w-4 mr-2 text-slate-400" />
                        Idioma
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      {isReadOnly ? (
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-2 text-slate-400" />
                          <span className="text-slate-700 dark:text-slate-300">
                            {formData.language || "Español"}
                          </span>
                        </div>
                      ) : (
                        <Select
                          options={languageOptions}
                          value={
                            languageOptions.find(
                              (option) => option.value === formData.language
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            setFormData({
                              ...formData,
                              language: selectedOption.value
                            });
                          }}
                          placeholder="Selecciona un idioma"
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: "white"
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: "white"
                            })
                          }}
                        />
                      )}
                    </div>
                    {/* Favorito */}
                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300 rounded">
                        Estado
                      </Label>
                      <div className="flex items-center space-x-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleToggleFavorite}
                          disabled={isReadOnly}
                          className={`flex items-center ${
                            formData.is_favorite
                              ? "text-amber-500 border-amber-500"
                              : "text-slate-500 border-slate-300 dark:border-slate-600"
                          }`}
                        >
                          {formData.is_favorite ? (
                            <>
                              <Star className="h-4 w-4 mr-1.5 fill-amber-500" />
                              Proveedor Favorito
                            </>
                          ) : (
                            <>
                              <StarOff className="h-4 w-4 mr-1.5" />
                              Marcar como Favorito
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Descripción */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="description"
                        className="text-slate-700 dark:text-slate-300 rounded"
                      >
                        Descripción
                      </Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Descripción del proveedor..."
                        className="bg-white dark:bg-slate-800 resize-none h-24"
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Contenido de la pestaña Calificaciones */}
              <TabsContent value="ratings" className="space-y-6">
                {mode === "view" ? (
                  <div className="space-y-6">
                    {/* Calificación general */}
                    <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-6">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
                        Calificación General
                      </h3>
                      <div className="flex items-center justify-center">
                        <div className="bg-orange-50 dark:bg-orange-900/20 px-6 py-4 rounded">
                          <div className="flex items-center justify-center">
                            <div className="flex items-center text-amber-500 mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-8 w-8 ${
                                    i < Math.round(formData.rating_overall)
                                      ? "fill-amber-500"
                                      : "fill-transparent"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-center text-3xl font-bold text-slate-900 dark:text-slate-100">
                            {formatRating(formData.rating_overall)}
                          </p>
                          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-1">
                            Calificación promedio
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Calificaciones específicas */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Servicio */}
                      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center mb-2">
                          <HeadphonesIcon className="h-5 w-5 text-blue-500 mr-2" />
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            Atención al Cliente
                          </h4>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-blue-500">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < Math.round(formData.rating_service)
                                    ? "fill-blue-500"
                                    : "fill-transparent"
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                            {formatRating(formData.rating_service)}
                          </p>
                        </div>
                      </div>

                      {/* Puntualidad */}
                      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center mb-2">
                          <Clock className="h-5 w-5 text-green-500 mr-2" />
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            Puntualidad
                          </h4>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-green-500">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < Math.round(formData.rating_punctuality)
                                    ? "fill-green-500"
                                    : "fill-transparent"
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                            {formatRating(formData.rating_punctuality)}
                          </p>
                        </div>
                      </div>

                      {/* Calidad */}
                      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center mb-2">
                          <BadgeCheck className="h-5 w-5 text-purple-500 mr-2" />
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            Calidad
                          </h4>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-purple-500">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < Math.round(formData.rating_quality)
                                    ? "fill-purple-500"
                                    : "fill-transparent"
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                            {formatRating(formData.rating_quality)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800/30 rounded p-4">
                      <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-center">
                        <Info className="h-4 w-4 mr-2" />
                        Las calificaciones se calculan automáticamente basándose
                        en el historial de pedidos y no se pueden editar
                        manualmente.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Calificación general - Solo lectura */}
                    <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-6">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
                        Calificación General
                      </h3>
                      <div className="flex items-center justify-center">
                        <div className="bg-orange-50 dark:bg-orange-900/20 px-6 py-4 rounded">
                          <p className="text-center text-3xl font-bold text-slate-900 dark:text-slate-100">
                            {formatRating(
                              (formData.rating_service +
                                formData.rating_punctuality +
                                formData.rating_quality) /
                                3
                            )}
                          </p>
                          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-1">
                            Promedio de todas las calificaciones
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Calificaciones específicas editables */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Servicio */}
                      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center mb-3">
                          <HeadphonesIcon className="h-5 w-5 text-blue-500 mr-2" />
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            Atención al Cliente
                          </h4>
                        </div>
                        <div className="flex items-center justify-center">
                          <div className="flex items-center text-blue-500">
                            {[...Array(5)].map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    rating_service: i + 1
                                  })
                                }
                                className="focus:outline-none p-1"
                              >
                                <Star
                                  className={`h-6 w-6 ${
                                    i < formData.rating_service
                                      ? "fill-blue-500"
                                      : "fill-transparent"
                                  } 
                                  hover:text-blue-600 transition-colors`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="text-center text-sm font-medium text-slate-700 dark:text-slate-300 mt-2">
                          {formatRating(formData.rating_service)}
                        </p>
                      </div>

                      {/* Puntualidad */}
                      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center mb-3">
                          <Clock className="h-5 w-5 text-green-500 mr-2" />
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            Puntualidad
                          </h4>
                        </div>
                        <div className="flex items-center justify-center">
                          <div className="flex items-center text-green-500">
                            {[...Array(5)].map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    rating_punctuality: i + 1
                                  })
                                }
                                className="focus:outline-none p-1"
                              >
                                <Star
                                  className={`h-6 w-6 ${
                                    i < formData.rating_punctuality
                                      ? "fill-green-500"
                                      : "fill-transparent"
                                  } 
                                  hover:text-green-600 transition-colors`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="text-center text-sm font-medium text-slate-700 dark:text-slate-300 mt-2">
                          {formatRating(formData.rating_punctuality)}
                        </p>
                      </div>

                      {/* Calidad */}
                      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center mb-3">
                          <BadgeCheck className="h-5 w-5 text-purple-500 mr-2" />
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            Calidad
                          </h4>
                        </div>
                        <div className="flex items-center justify-center">
                          <div className="flex items-center text-purple-500">
                            {[...Array(5)].map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    rating_quality: i + 1
                                  })
                                }
                                className="focus:outline-none p-1"
                              >
                                <Star
                                  className={`h-6 w-6 ${
                                    i < formData.rating_quality
                                      ? "fill-purple-500"
                                      : "fill-transparent"
                                  } 
                                  hover:text-purple-600 transition-colors`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="text-center text-sm font-medium text-slate-700 dark:text-slate-300 mt-2">
                          {formatRating(formData.rating_quality)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center">
                        <Info className="h-4 w-4 mr-2" />
                        Haz clic en las estrellas para establecer calificaciones
                        iniciales para este proveedor. La calificación general
                        se calculará como el promedio de estas tres
                        calificaciones.
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Pie de modal */}
          <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-between">
            {/* Botones de acción izquierda */}
            <div>
              {(mode === "view" || mode === "edit") && supplier && (
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(true)}
                  className="border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash className="h-4 w-4 mr-2" />
                  Eliminar
                </Button>
              )}
            </div>

            {/* Botones de acción derecha */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-slate-300 dark:border-slate-600"
              >
                Cancelar
              </Button>

              {mode !== "view" && (
                <Button
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Diálogo de confirmación para eliminar */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-900 dark:text-slate-100">
                ¿Eliminar proveedor?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                Esta acción no se puede deshacer. El proveedor será eliminado
                permanentemente de nuestros servidores.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isSubmitting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Eliminar
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AnimatePresence>
  );
} 